/**
 * persistence/db/schema.js
 *
 * Dexie schema definitions for all IndexedDB object stores.
 *
 * Dexie index notation:
 *   ++id         auto-increment primary key
 *   &field       unique index
 *   [a+b]        compound index  (queryable as { a, b })
 *   *field       multi-entry index (for arrays)
 *
 * Rule: only index fields you actually query on.  Unindexed fields are still
 * stored and readable — they just can't be used in .where() clauses.
 *
 * Each schema version is additive.  When you need to change the schema:
 *   1. Increment DB_VERSION in config.js
 *   2. Add a new key in SCHEMA_VERSIONS for that version
 *   3. Add an upgrade() callback in database.js if data migration is needed
 */

import { DB_VERSION } from "../config";

/**
 * Schema for every database version.
 * Dexie.version(n).stores(SCHEMA_VERSIONS[n]) is called in database.js.
 */
export const SCHEMA_VERSIONS = {
  /**
   * v1 — initial schema
   */
  1: {
    /**
     * sessions — one live record per (matchId + analystId) pair.
     *
     * Updated on every debounced IndexedDB write (every ~200 ms during tagging).
     * This is the primary crash-recovery source: on InputBoard mount, we compare
     * updatedAt vs lastApiSyncAt to decide whether to offer recovery.
     *
     * Indexed fields:
     *   [matchId+analystId]  compound key for fast session lookup
     *   matchId              for listing all sessions of a match
     *   analystId            for listing all sessions by an analyst
     *   updatedAt            for pruning/sorting by recency
     *   status               for filtering active vs ended sessions
     */
    sessions:
      "++id, [matchId+analystId], matchId, analystId, updatedAt, status",

    /**
     * versions — checkpoint snapshots (full event string at a point in time).
     *
     * A new record is appended whenever CheckpointManager fires.
     * triggerType identifies what caused the snapshot:
     *   'time'        — 5-minute interval elapsed
     *   'event-count' — N new events added since last checkpoint
     *   'api-success' — REST API push succeeded
     *   'manual'      — user explicitly requested a save
     *   'exit'        — beforeunload / visibilitychange fired
     *
     * Indexed fields:
     *   [matchId+analystId]  lookup all versions for a session
     *   matchId              for cross-session queries
     *   createdAt            for time-ordered queries and pruning
     *   triggerType          for filtering by cause
     */
    versions:
      "++id, [matchId+analystId], matchId, analystId, createdAt, triggerType",

    /**
     * diffLog — append-only record of every meaningful string change.
     *
     * Each record stores only the delta (added/removed events), not the full
     * string.  This keeps the table lightweight while giving fine-grained
     * history for future per-event undo.
     *
     * Indexed fields:
     *   [matchId+analystId]  lookup diff history for a session
     *   timestamp            for time-ordered queries and pruning
     */
    diffLog: "++id, [matchId+analystId], matchId, analystId, timestamp",

    /**
     * syncQueue — pending REST API push requests.
     *
     * The SyncManager dequeues and processes these.  Entries survive page
     * refreshes — if the push was queued but the tab closed before it
     * succeeded, the next session start will find and retry it.
     *
     * status values: 'pending' | 'processing' | 'done' | 'failed'
     *
     * Indexed fields:
     *   [matchId+analystId]  find all pending pushes for a session
     *   status               dequeue by status
     *   queuedAt             for pruning old 'done' entries
     *   nextAttemptAt        for scheduling retries
     */
    syncQueue:
      "++id, [matchId+analystId], matchId, analystId, status, queuedAt, nextAttemptAt",

    /**
     * firestoreQueue — pending Firestore writes.
     *
     * type values:
     *   'session-backup'  — live currentString update
     *   'checkpoint'      — version snapshot
     *   'diff'            — diffLog batch
     *
     * status values: 'pending' | 'processing' | 'done' | 'failed'
     */
    firestoreQueue:
      "++id, [matchId+analystId], matchId, analystId, type, status, queuedAt",
  },

  /**
   * v2 — git-style version control layer (Yjs).
   *
   * Additive: every v1 store is repeated verbatim (Dexie requires the full
   * store set for each version), plus two new stores for the VCS layer.
   *
   * The event string is modelled as a Y.Array<string> inside a Y.Doc.  Each
   * analyst's working copy is a "branch"; point-in-time snapshots are "commits".
   * Yjs state is stored as a binary update blob (Uint8Array from
   * Y.encodeStateAsUpdate) — Dexie persists Uint8Array natively via structured
   * clone, so no base64 is needed for the local store.
   */
  2: {
    // ── v1 stores (unchanged) ───────────────────────────────────────────────
    sessions:
      "++id, [matchId+analystId], matchId, analystId, updatedAt, status",
    versions:
      "++id, [matchId+analystId], matchId, analystId, createdAt, triggerType",
    diffLog: "++id, [matchId+analystId], matchId, analystId, timestamp",
    syncQueue:
      "++id, [matchId+analystId], matchId, analystId, status, queuedAt, nextAttemptAt",
    firestoreQueue:
      "++id, [matchId+analystId], matchId, analystId, type, status, queuedAt",

    /**
     * branches — one row per (matchId, analystId, branchName) working copy.
     *
     * Holds the live HEAD Yjs state for that branch.  The default branch is
     * created lazily on first edit (see BranchManager.getOrCreateDefaultBranch).
     *
     * Fields:
     *   branchName       e.g. "me/123" (own) or "123/main" when pulled in
     *   baseBranchName   branch this was forked/pulled from (null for default)
     *   headCommitId     commitId of the latest commit on this branch (or null)
     *   stateUpdate      Uint8Array — Y.encodeStateAsUpdate(doc) for the HEAD
     *   updatedAt        epoch ms of the last reconcile/commit
     *
     * Indexed:
     *   &[matchId+analystId+branchName]  unique — one row per branch
     *   [matchId+analystId]              list all branches for a session
     */
    branches:
      "++id, &[matchId+analystId+branchName], [matchId+analystId], matchId, analystId, updatedAt",

    /**
     * commits — immutable Yjs snapshots with git-style metadata.
     *
     * A commit stores the full encoded Y.Doc state at commit time plus the
     * parent linkage so the history forms a DAG (merge commits have two
     * parents joined by "+").
     *
     * Fields:
     *   commitId         stable unique id (uuid-ish, generated in BranchManager)
     *   branchId         FK → branches.id
     *   branchName       denormalised for cross-branch queries
     *   parentCommitId   previous commit on the branch (null for root); merge
     *                    commits store "parentA+parentB"
     *   message          human commit message / trigger label
     *   authorAnalystId  who created the commit
     *   stateUpdate      Uint8Array — Y.encodeStateAsUpdate(doc) snapshot
     *   eventCount       meaningful event count at commit time
     *   diffSummary      { added, removed } vs parent
     *   createdAt        epoch ms
     *   pushed           boolean — whether mirrored to Firestore
     *
     * Indexed:
     *   &commitId                        unique lookup
     *   [matchId+analystId+branchName]   list a branch's commits
     *   branchId / parentCommitId        DAG traversal
     *   createdAt                        time-ordered / pruning
     */
    commits:
      "++id, &commitId, [matchId+analystId+branchName], branchId, parentCommitId, matchId, analystId, createdAt",
  },

  /**
   * v3 — reviewer-portal local snapshots.
   *
   * Additive: every v2 store repeated verbatim, plus one new store capturing
   * point-in-time snapshots of a reviewer's full review state (the eventStrings
   * array with per-string status/comments). Local-only — never synced to cloud.
   */
  3: {
    // ── v2 stores (unchanged) ───────────────────────────────────────────────
    sessions:
      "++id, [matchId+analystId], matchId, analystId, updatedAt, status",
    versions:
      "++id, [matchId+analystId], matchId, analystId, createdAt, triggerType",
    diffLog: "++id, [matchId+analystId], matchId, analystId, timestamp",
    syncQueue:
      "++id, [matchId+analystId], matchId, analystId, status, queuedAt, nextAttemptAt",
    firestoreQueue:
      "++id, [matchId+analystId], matchId, analystId, type, status, queuedAt",
    branches:
      "++id, &[matchId+analystId+branchName], [matchId+analystId], matchId, analystId, updatedAt",
    commits:
      "++id, &commitId, [matchId+analystId+branchName], branchId, parentCommitId, matchId, analystId, createdAt",

    /**
     * reviewerVersions — local snapshots of a reviewer's review state.
     *
     * One row per snapshot. `eventStrings` holds the full (unindexed) review
     * list at snapshot time; Dexie persists the array natively via structured
     * clone. `summary` denormalises counts for fast list rendering.
     *
     * Fields:
     *   matchId, reviewerId   session key (reviewerId = logged-in reviewer)
     *   createdAt             epoch ms (time-ordered / pruning)
     *   label                 human label (e.g. "Marked error: <string>")
     *   summary               { total, reviewed, errors, pending }
     *   eventStrings          [{ value, status, comments, error, isFixed }]
     */
    reviewerVersions:
      "++id, [matchId+reviewerId], matchId, reviewerId, createdAt",
  },
};

/**
 * Returns the stores definition for a given version number.
 * Throws if the version is not defined — catches config/schema mismatches early.
 */
export function getSchemaForVersion(version) {
  const schema = SCHEMA_VERSIONS[version];
  if (!schema) {
    throw new Error(
      `[persistence] No schema defined for DB version ${version}. ` +
        `Update SCHEMA_VERSIONS in db/schema.js.`
    );
  }
  return schema;
}

export { DB_VERSION };
